import { ApolloError, ServerError } from "@apollo/client/core";
import { IMessage, IMessageContext } from "@saleor/components/messages";
import { apolloClient } from "@saleor/graphql/client";
import { UseNotifierResult } from "@saleor/hooks/useNotifier";
import { commonMessages } from "@saleor/intl";
import { getMutationErrors, parseLogMessage } from "@saleor/misc";
import { Auth } from "aws-amplify";
import { IntlShape } from "react-intl";

import { isJwtError, isTokenExpired } from "./errors";

export const displayDemoMessage = (
  intl: IntlShape,
  notify: UseNotifierResult
) => {
  notify({
    text: intl.formatMessage(commonMessages.demo)
  });
};

const getNetworkErrors = (error: ApolloError): string[] => {
  const networkErrors = error.networkError as ServerError;

  if (networkErrors) {
    // Apparently network errors can be an object or an array
    if (Array.isArray(networkErrors.result)) {
      networkErrors.result.forEach(result => {
        if (result.errors) {
          return result.errors.map(({ message }) => message);
        }
      });
    }

    return [networkErrors.message];
  }

  return [];
};

const getAllErrorMessages = (error: ApolloError) => [
  ...(error.graphQLErrors?.map(err => err.message) || []),
  ...getNetworkErrors(error)
];

export const showAllErrors = ({
  notify,
  error,
  sourceSystem
}: {
  notify: IMessageContext;
  error: ApolloError;
  sourceSystem?: string;
}) => {
  getAllErrorMessages(error).forEach(message => {
    if (message === "Signature verification failed") {
      notify({
        status: "error",
        sourceSystem: "Eordering",
        apiMessage: message
      });
    } else {
      notify({
        status: "error",
        sourceSystem,
        apiMessage: message
      });
    }
  });
};

export const handleNestedMutationErrors = ({
  data,
  intl,
  notify
}: {
  data: any;
  intl: IntlShape;
  notify: (message: IMessage) => void;
}) => {
  const mutationErrors = getMutationErrors({ data });

  if (mutationErrors.length > 0) {
    mutationErrors.forEach(error => {
      notify({
        status: "error",
        text: error.message,
        apiMessage: parseLogMessage({
          intl,
          code: error.code,
          field: error.field
        })
      });
    });
  }
};

export async function handleQueryAuthError(
  error: ApolloError,
  notify: IMessageContext,
  intl: IntlShape,
  tokenRefresh: () => Promise<boolean>,
  sourceSystem?: string
) {
  if (
    error.graphQLErrors.some(isJwtError) &&
    error.graphQLErrors.every(isTokenExpired)
  ) {
    const success = await tokenRefresh();
    if (!success) {
      notify({
        status: "error",
        text: intl.formatMessage(commonMessages.sessionExpired)
      });
      removeTokens();
      await Auth.signOut();
      await apolloClient.clearStore();
      return false;
    }
    return true;
  } else {
    showAllErrors({ notify, error, sourceSystem });
    return false;
  }
}

export enum TOKEN_STORAGE_KEY {
  AUTH = "auth",
  CSRF = "csrf",
  REFRESH = "refresh"
}

export const getTokens = () => ({
  auth:
    localStorage.getItem(TOKEN_STORAGE_KEY.AUTH) ||
    sessionStorage.getItem(TOKEN_STORAGE_KEY.AUTH),
  refresh:
    localStorage.getItem(TOKEN_STORAGE_KEY.CSRF) ||
    sessionStorage.getItem(TOKEN_STORAGE_KEY.CSRF),
  csrf:
    localStorage.getItem(TOKEN_STORAGE_KEY.REFRESH) ||
    sessionStorage.getItem(TOKEN_STORAGE_KEY.REFRESH)
});

export const setTokens = (
  auth: string,
  csrf: string,
  refresh: string,
  persist: boolean
) => {
  if (persist) {
    localStorage.setItem(TOKEN_STORAGE_KEY.AUTH, auth);
    localStorage.setItem(TOKEN_STORAGE_KEY.CSRF, csrf);
    localStorage.setItem(TOKEN_STORAGE_KEY.REFRESH, refresh);
  } else {
    sessionStorage.setItem(TOKEN_STORAGE_KEY.AUTH, auth);
    sessionStorage.setItem(TOKEN_STORAGE_KEY.CSRF, csrf);
    sessionStorage.setItem(TOKEN_STORAGE_KEY.REFRESH, refresh);
  }
};

export const removeTokens = () => {
  localStorage.removeItem(TOKEN_STORAGE_KEY.AUTH);
  localStorage.removeItem(TOKEN_STORAGE_KEY.CSRF);
  sessionStorage.removeItem(TOKEN_STORAGE_KEY.AUTH);
  sessionStorage.removeItem(TOKEN_STORAGE_KEY.CSRF);
  sessionStorage.removeItem(TOKEN_STORAGE_KEY.REFRESH);
  sessionStorage.removeItem("previousRole");
};

export const setAuthToken = (auth: string, persist: boolean) => {
  if (persist) {
    localStorage.setItem(TOKEN_STORAGE_KEY.AUTH, auth);
  } else {
    sessionStorage.setItem(TOKEN_STORAGE_KEY.AUTH, auth);
  }
};

export const isAuthTokenExpired = (authToken?: string) => {
  const token = authToken ?? getTokens().auth;
  if (!token) {
    return true;
  }
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    return payload?.exp * 1000 <= Date.now();
  } catch (error) {
    return true;
  }
};
